import { Link, useOutletContext } from 'react-router';
import type { SectionDefinition } from '../sections/types';
import { groupByCategory } from '../sections/groupByCategory';
import type { HubOutletContext } from './HubShell';

/**
 * The `/` route: every section the signed-in user can see, as cards grouped the same way the
 * sidebar groups them. `visibleSections` comes from `HubShell` via `useOutletContext` rather than
 * being recomputed here — one access check, shared by both.
 */
export function HomePage() {
  const { visibleSections } = useOutletContext<HubOutletContext>();

  const uncategorized = visibleSections.filter((s) => s.category === null);
  const categories = groupByCategory(visibleSections);

  return (
    <div className="home">
      <div className="home__intro">
        <h1>Home</h1>
        <p>Pick a section below, or from the sidebar.</p>
      </div>

      {visibleSections.length === 0 ? (
        <p className="home__empty">
          You don't have access to any sections yet. Ask an administrator to add you to the group a
          section requires.
        </p>
      ) : (
        <>
          {uncategorized.length > 0 && (
            <div className="home__group">
              <SectionCards sections={uncategorized} />
            </div>
          )}

          {[...categories.entries()].map(([category, items]) => (
            <div className="home__group" key={category}>
              <h2 className="home__category">{category}</h2>
              <SectionCards sections={items} />
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function SectionCards({ sections }: { sections: SectionDefinition[] }) {
  return (
    <div className="home-grid">
      {sections.map((section) => (
        <Link key={section.id} to={section.basePath} className="home-card">
          <h3 className="home-card__title">{section.navLabel}</h3>
          <p className="home-card__description">{section.description}</p>
        </Link>
      ))}
    </div>
  );
}
